import React, { useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { useTheme } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CheckIcon from '@material-ui/icons/Check'
import HomeWorkIcon from '@material-ui/icons/HomeWork'
import PersonIcon from '@material-ui/icons/Person'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import NotesIcon from '@material-ui/icons/Notes'

import {
    useAvailableEntrataProperties,
    useSelfServeEntrataOnboard,
} from '../../../hooks/useSelfServeEntrataOnboard'
import { AvailableEntrataProperty, FormUser } from '../../../models/Forms'
import { StyledFormContainer } from '../styles'
import { FormUsersSection, hasIncompleteFormUsers } from '../FormUsersSection'

interface Props {
    organization: number
}

const STEPS = ['Properties', 'Users', 'Details', 'Review']

const STEP_PROPERTIES = 0
const STEP_USERS = 1
const STEP_DETAILS = 2
const STEP_REVIEW = 3

const emptyUser: FormUser = { name: '', email: '' }

const formatAddress = (prop: AvailableEntrataProperty) =>
    [prop.address, prop.city, prop.state, prop.zip_code]
        .filter(Boolean)
        .join(', ')

const initialsFor = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export const EntrataOnboardWizard = ({ organization }: Props) => {
    const theme = useTheme()
    const [activeStep, setActiveStep] = useState(STEP_PROPERTIES)
    const [search, setSearch] = useState('')
    const [showOnboarded, setShowOnboarded] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [showValidation, setShowValidation] = useState(false)
    const [regionalManager, setRegionalManager] = useState<FormUser>(emptyUser)
    const [propertyManager, setPropertyManager] = useState<FormUser>(emptyUser)
    const [otherUsers, setOtherUsers] = useState<FormUser[]>([])
    const [billingEmail, setBillingEmail] = useState('')
    const [notes, setNotes] = useState('')

    const {
        data: properties = [],
        isLoading: propertiesLoading,
        isError: propertiesError,
        error: propertiesErrorDetail,
        refetch,
    } = useAvailableEntrataProperties(organization, true)

    const { onboard, isLoading: submitting } = useSelfServeEntrataOnboard()

    const selectedProperties = useMemo(
        () => properties.filter((prop) => selectedIds.has(prop.entrata_id)),
        [properties, selectedIds],
    )

    const filteredProperties = useMemo(() => {
        const term = search.trim().toLowerCase()
        const searched = term
            ? properties.filter((prop) => {
                  const haystack = `${prop.name} ${prop.address} ${prop.city} ${prop.state}`.toLowerCase()
                  return haystack.includes(term)
              })
            : properties
        const visible = showOnboarded
            ? searched
            : searched.filter((prop) => !prop.already_onboarded)
        return [...visible].sort((a, b) => {
            if (a.already_onboarded !== b.already_onboarded) {
                return a.already_onboarded ? 1 : -1
            }
            return a.name.localeCompare(b.name)
        })
    }, [properties, search, showOnboarded])

    const selectableProperties = filteredProperties.filter(
        (prop) => !prop.already_onboarded,
    )
    const onboardedCount = properties.filter((p) => p.already_onboarded).length
    const availableCount = properties.length - onboardedCount

    const toggleProperty = (prop: AvailableEntrataProperty) => {
        if (prop.already_onboarded) {
            return
        }
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(prop.entrata_id)) {
                next.delete(prop.entrata_id)
            } else {
                next.add(prop.entrata_id)
            }
            return next
        })
    }

    const allSelectableSelected =
        selectableProperties.length > 0 &&
        selectableProperties.every((prop) => selectedIds.has(prop.entrata_id))

    const toggleSelectAllVisible = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (allSelectableSelected) {
                selectableProperties.forEach((prop) =>
                    next.delete(prop.entrata_id),
                )
            } else {
                selectableProperties.forEach((prop) =>
                    next.add(prop.entrata_id),
                )
            }
            return next
        })
    }

    const usersValue = {
        regional_manager: regionalManager,
        property_manager: propertyManager,
        other_users: otherUsers,
    }

    const invitedUsers = [
        {
            role: 'Regional Manager',
            name: regionalManager.name,
            email: regionalManager.email,
        },
        ...(propertyManager.name && propertyManager.email
            ? [
                  {
                      role: 'Property Manager',
                      name: propertyManager.name,
                      email: propertyManager.email,
                  },
              ]
            : []),
        ...otherUsers
            .filter((user) => user.name && user.email)
            .map((user) => ({
                role: 'User',
                name: user.name,
                email: user.email,
            })),
    ]

    const canAdvance = (): boolean => {
        if (activeStep === STEP_PROPERTIES) {
            return selectedIds.size > 0
        }
        if (activeStep === STEP_USERS) {
            return !hasIncompleteFormUsers(usersValue)
        }
        if (activeStep === STEP_DETAILS) {
            return Boolean(billingEmail.trim())
        }
        return true
    }

    const handleNext = () => {
        if (activeStep === STEP_USERS && hasIncompleteFormUsers(usersValue)) {
            setShowValidation(true)
            return
        }
        if (activeStep === STEP_DETAILS && !billingEmail.trim()) {
            setShowValidation(true)
            return
        }
        setShowValidation(false)
        setActiveStep((step) => Math.min(step + 1, STEP_REVIEW))
    }

    const handleBack = () => {
        setActiveStep((step) => Math.max(step - 1, STEP_PROPERTIES))
    }

    const handleSubmit = async () => {
        if (
            !canAdvance() ||
            selectedProperties.length === 0 ||
            !billingEmail.trim()
        ) {
            setShowValidation(true)
            return
        }

        const validOtherUsers = otherUsers.filter(
            (user) => user.name && user.email,
        )

        await onboard({
            organization,
            properties: selectedProperties.map((prop) => ({
                entrata_id: prop.entrata_id,
                name: prop.name,
                address: prop.address,
                city: prop.city,
                state: prop.state,
                zip_code: prop.zip_code,
            })),
            regional_manager: regionalManager,
            property_manager:
                propertyManager.name && propertyManager.email
                    ? propertyManager
                    : undefined,
            other_users: validOtherUsers.length ? validOtherUsers : undefined,
            billing_email: billingEmail.trim(),
            start_date: new Date().toISOString().slice(0, 10),
            notes: notes || null,
        }).then(() => {
            setSelectedIds(new Set())
            setRegionalManager(emptyUser)
            setPropertyManager(emptyUser)
            setOtherUsers([])
            setBillingEmail('')
            setNotes('')
            setShowValidation(false)
            setActiveStep(STEP_PROPERTIES)
        })
    }

    const errorMessage =
        (propertiesErrorDetail as any)?.response?.data?.message ||
        'Unable to load Entrata properties.'

    const primaryButtonStyle: React.CSSProperties = {
        textTransform: 'none',
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        minWidth: 140,
        fontWeight: 600,
    }

    const stepCopy = [
        'Select the Entrata properties you want to add to EZOS.',
        'Tell us who should have access when the properties are created.',
        'Where should invoices go, and anything else support should know?',
        'Confirm everything below. Onboarding starts as soon as you confirm.',
    ]

    const summaryCardStyle: React.CSSProperties = {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
    }

    return (
        <StyledFormContainer maxWidth="lg">
            <Box style={{ flexShrink: 0, marginBottom: theme.spacing(1) }}>
                <Typography
                    variant="h5"
                    style={{
                        fontWeight: 700,
                        color: theme.palette.darkBlue.main,
                    }}
                >
                    Onboard from Entrata
                </Typography>
                <Typography
                    variant="body2"
                    style={{
                        marginTop: 4,
                        color: theme.palette.text.secondary,
                    }}
                >
                    {stepCopy[activeStep]}
                </Typography>
            </Box>

            <Stepper
                activeStep={activeStep}
                alternativeLabel
                style={{
                    margin: `${theme.spacing(1)}px 0 ${theme.spacing(2)}px`,
                    flexShrink: 0,
                    backgroundColor: 'transparent',
                    padding: theme.spacing(1, 0, 0),
                }}
            >
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <div className="form-content">
                <div className="wizard-section">
                    {activeStep === STEP_PROPERTIES && (
                        <>
                            {propertiesLoading && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    flex={1}
                                >
                                    <CircularProgress />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginTop: 16 }}
                                    >
                                        Loading properties from Entrata…
                                    </Typography>
                                </Box>
                            )}
                            {propertiesError && (
                                <Alert
                                    severity="error"
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() => refetch()}
                                        >
                                            Retry
                                        </Button>
                                    }
                                >
                                    {errorMessage}
                                </Alert>
                            )}
                            {!propertiesLoading && !propertiesError && (
                                <>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{
                                            gap: theme.spacing(2),
                                            flexShrink: 0,
                                            marginBottom: theme.spacing(1.5),
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <TextField
                                            size="small"
                                            placeholder="Search by name or address"
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                            style={{
                                                flex: 1,
                                                minWidth: 220,
                                                marginBottom: 0,
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    size="small"
                                                    checked={showOnboarded}
                                                    onChange={(e) =>
                                                        setShowOnboarded(
                                                            e.target.checked,
                                                        )
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Typography variant="body2">
                                                    Show already in EZOS
                                                    {onboardedCount > 0
                                                        ? ` (${onboardedCount})`
                                                        : ''}
                                                </Typography>
                                            }
                                        />
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{
                                            flexShrink: 0,
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        {availableCount} available
                                        {onboardedCount > 0
                                            ? ` · ${onboardedCount} already onboarded`
                                            : ''}
                                    </Typography>
                                    <div className="wizard-table-wrap">
                                        <div className="wizard-list">
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                color="primary"
                                                                checked={
                                                                    allSelectableSelected
                                                                }
                                                                indeterminate={
                                                                    selectableProperties.some(
                                                                        (
                                                                            prop,
                                                                        ) =>
                                                                            selectedIds.has(
                                                                                prop.entrata_id,
                                                                            ),
                                                                    ) &&
                                                                    !allSelectableSelected
                                                                }
                                                                onChange={
                                                                    toggleSelectAllVisible
                                                                }
                                                                disabled={
                                                                    selectableProperties.length ===
                                                                    0
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            Property
                                                        </TableCell>
                                                        <TableCell>
                                                            Address
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            Status
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredProperties.length ===
                                                        0 && (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={4}
                                                                style={{
                                                                    padding: theme.spacing(
                                                                        6,
                                                                    ),
                                                                    textAlign:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Typography color="textSecondary">
                                                                    {search
                                                                        ? 'No properties match your search.'
                                                                        : showOnboarded
                                                                        ? 'No Entrata properties found.'
                                                                        : 'No new properties to onboard.'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    {filteredProperties.map(
                                                        (prop) => {
                                                            const selected = selectedIds.has(
                                                                prop.entrata_id,
                                                            )
                                                            return (
                                                                <TableRow
                                                                    key={
                                                                        prop.entrata_id
                                                                    }
                                                                    hover={
                                                                        !prop.already_onboarded
                                                                    }
                                                                    selected={
                                                                        selected
                                                                    }
                                                                    onClick={() =>
                                                                        toggleProperty(
                                                                            prop,
                                                                        )
                                                                    }
                                                                    style={{
                                                                        cursor: prop.already_onboarded
                                                                            ? 'default'
                                                                            : 'pointer',
                                                                        opacity: prop.already_onboarded
                                                                            ? 0.55
                                                                            : 1,
                                                                    }}
                                                                >
                                                                    <TableCell padding="checkbox">
                                                                        <Checkbox
                                                                            color="primary"
                                                                            checked={
                                                                                selected
                                                                            }
                                                                            disabled={
                                                                                prop.already_onboarded
                                                                            }
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography
                                                                            variant="body2"
                                                                            style={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                prop.name
                                                                            }
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="textSecondary"
                                                                        >
                                                                            {formatAddress(
                                                                                prop,
                                                                            ) ||
                                                                                '—'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        {prop.already_onboarded ? (
                                                                            <Chip
                                                                                size="small"
                                                                                label="In EZOS"
                                                                            />
                                                                        ) : selected ? (
                                                                            <Chip
                                                                                size="small"
                                                                                color="primary"
                                                                                label="Selected"
                                                                            />
                                                                        ) : (
                                                                            <Chip
                                                                                size="small"
                                                                                variant="outlined"
                                                                                label="Available"
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        },
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {activeStep === STEP_USERS && (
                        <div className="wizard-list">
                            <Paper elevation={0} style={summaryCardStyle}>
                                <Grid container spacing={2}>
                                    <FormUsersSection
                                        variant="wizard"
                                        value={usersValue}
                                        showValidation={showValidation}
                                        onManagerChange={(
                                            managerType,
                                            field,
                                            value,
                                        ) => {
                                            if (
                                                managerType ===
                                                'regional_manager'
                                            ) {
                                                setRegionalManager((prev) => ({
                                                    ...prev,
                                                    [field]: value,
                                                }))
                                            } else {
                                                setPropertyManager((prev) => ({
                                                    ...prev,
                                                    [field]: value,
                                                }))
                                            }
                                        }}
                                        onOtherUserChange={(
                                            index,
                                            field,
                                            value,
                                        ) => {
                                            setOtherUsers((prev) =>
                                                prev.map((user, i) =>
                                                    i === index
                                                        ? {
                                                              ...user,
                                                              [field]: value,
                                                          }
                                                        : user,
                                                ),
                                            )
                                        }}
                                        onAddOtherUser={() =>
                                            setOtherUsers((prev) => [
                                                ...prev,
                                                { name: '', email: '' },
                                            ])
                                        }
                                        onRemoveOtherUser={(index) =>
                                            setOtherUsers((prev) =>
                                                prev.filter(
                                                    (_, i) => i !== index,
                                                ),
                                            )
                                        }
                                    />
                                </Grid>
                            </Paper>
                        </div>
                    )}

                    {activeStep === STEP_DETAILS && (
                        <div className="wizard-list">
                            <Paper elevation={0} style={summaryCardStyle}>
                                <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: 600, marginBottom: 4 }}
                                >
                                    Billing
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    display="block"
                                    style={{ marginBottom: 12 }}
                                >
                                    Required. Invoices for these properties will
                                    be sent here.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="email"
                                            label="Billing Email"
                                            value={billingEmail}
                                            onChange={(e) =>
                                                setBillingEmail(e.target.value)
                                            }
                                            required
                                            error={
                                                showValidation &&
                                                !billingEmail.trim()
                                            }
                                            helperText={
                                                showValidation &&
                                                !billingEmail.trim()
                                                    ? 'Billing email is required'
                                                    : ' '
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={4}
                                            label="Notes"
                                            value={notes}
                                            onChange={(e) =>
                                                setNotes(e.target.value)
                                            }
                                            helperText="Optional. Visible to EZOS support."
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </div>
                    )}

                    {activeStep === STEP_REVIEW && (
                        <div className="wizard-list">
                            <Paper
                                elevation={0}
                                style={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <Box
                                    style={{
                                        padding: theme.spacing(2.5, 3),
                                        backgroundColor: '#F7F9F8',
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        style={{
                                            fontWeight: 700,
                                            color: theme.palette.darkBlue.main,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        Ready to onboard
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginTop: 4 }}
                                    >
                                        These properties will be created in EZOS
                                        now. Invites go out to the people below.
                                    </Typography>
                                    <Box
                                        display="flex"
                                        style={{
                                            gap: 8,
                                            marginTop: 12,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            icon={
                                                <HomeWorkIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            }
                                            label={
                                                selectedProperties.length === 1
                                                    ? '1 property'
                                                    : `${selectedProperties.length} properties`
                                            }
                                        />
                                        <Chip
                                            size="small"
                                            icon={
                                                <PersonIcon
                                                    style={{ fontSize: 16 }}
                                                />
                                            }
                                            label={
                                                invitedUsers.length === 1
                                                    ? '1 person invited'
                                                    : `${invitedUsers.length} people invited`
                                            }
                                        />
                                    </Box>
                                </Box>

                                <Grid container>
                                    <Grid
                                        item
                                        xs={12}
                                        md={7}
                                        style={{
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box
                                            style={{
                                                padding: theme.spacing(2, 3, 1),
                                            }}
                                        >
                                            <Typography
                                                variant="overline"
                                                style={{
                                                    letterSpacing: 0.8,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            >
                                                Properties
                                            </Typography>
                                        </Box>
                                        {selectedProperties.map((prop) => (
                                            <Box
                                                key={prop.entrata_id}
                                                display="flex"
                                                alignItems="flex-start"
                                                style={{
                                                    gap: 12,
                                                    padding: theme.spacing(
                                                        1.5,
                                                        3,
                                                    ),
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 8,
                                                        backgroundColor:
                                                            '#E8F3F1',
                                                        color:
                                                            theme.palette
                                                                .primary.dark,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <HomeWorkIcon
                                                        style={{
                                                            fontSize: 18,
                                                        }}
                                                    />
                                                </Box>
                                                <Box minWidth={0}>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {prop.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        {formatAddress(prop) ||
                                                            '—'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Box
                                            style={{
                                                padding: theme.spacing(2, 3, 1),
                                            }}
                                        >
                                            <Typography
                                                variant="overline"
                                                style={{
                                                    letterSpacing: 0.8,
                                                    color:
                                                        theme.palette.text
                                                            .secondary,
                                                }}
                                            >
                                                People
                                            </Typography>
                                        </Box>
                                        {invitedUsers.map((user, index) => (
                                            <Box
                                                key={`${user.email}-${index}`}
                                                display="flex"
                                                alignItems="center"
                                                style={{
                                                    gap: 12,
                                                    padding: theme.spacing(
                                                        1.5,
                                                        3,
                                                    ),
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            theme.palette
                                                                .primary.dark,
                                                        color:
                                                            theme.palette
                                                                .primary
                                                                .contrastText,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {initialsFor(user.name)}
                                                </Box>
                                                <Box minWidth={0} flex={1}>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                        noWrap
                                                    >
                                                        {user.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        noWrap
                                                        display="block"
                                                    >
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    label={user.role}
                                                    variant="outlined"
                                                />
                                            </Box>
                                        ))}
                                    </Grid>
                                </Grid>

                                <Box
                                    style={{
                                        padding: theme.spacing(2, 3),
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: '#F7F9F8',
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={notes ? 6 : 12}>
                                            <Box
                                                display="flex"
                                                alignItems="flex-start"
                                                style={{ gap: 10 }}
                                            >
                                                <MailOutlineIcon
                                                    style={{
                                                        fontSize: 18,
                                                        marginTop: 2,
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                    }}
                                                />
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Billing email
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        style={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {billingEmail}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        {notes ? (
                                            <Grid item xs={12} sm={6}>
                                                <Box
                                                    display="flex"
                                                    alignItems="flex-start"
                                                    style={{ gap: 10 }}
                                                >
                                                    <NotesIcon
                                                        style={{
                                                            fontSize: 18,
                                                            marginTop: 2,
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Notes
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {notes}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ) : null}
                                    </Grid>
                                </Box>
                            </Paper>
                        </div>
                    )}

                    <div className="wizard-footer">
                        <Button
                            onClick={handleBack}
                            disabled={
                                activeStep === STEP_PROPERTIES || submitting
                            }
                            startIcon={<ArrowBackIcon />}
                            style={{ textTransform: 'none' }}
                        >
                            Back
                        </Button>
                        {activeStep === STEP_PROPERTIES ? (
                            <Chip
                                color={
                                    selectedIds.size > 0 ? 'primary' : 'default'
                                }
                                label={
                                    selectedIds.size === 1
                                        ? '1 property selected'
                                        : `${selectedIds.size} properties selected`
                                }
                            />
                        ) : (
                            <span />
                        )}
                        {activeStep < STEP_REVIEW ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                                disabled={
                                    (activeStep === STEP_PROPERTIES &&
                                        selectedIds.size === 0) ||
                                    propertiesLoading
                                }
                                style={primaryButtonStyle}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={submitting}
                                startIcon={
                                    submitting ? (
                                        <CircularProgress
                                            size={16}
                                            color="inherit"
                                        />
                                    ) : (
                                        <CheckIcon />
                                    )
                                }
                                style={primaryButtonStyle}
                            >
                                {submitting
                                    ? 'Starting…'
                                    : 'Confirm and onboard'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </StyledFormContainer>
    )
}
